import React, { useEffect, useReducer, useRef, useState } from 'react';
import BlockSection, { itemDomId } from './BlockSection';
import ResultModal from './ResultModal';
import DoutoBanner from '../DoutoBanner';
import { doutoBannersEnabled } from '../../lib/flags';
import { ScorePanel, StickyRuler } from './ScoreRuler';
import { MEEM_BLOCKS, MEEM_TOTAL_MAX } from '../../lib/meemItems';
import {
  blockScoresFrom, blockStatus, effectiveEducation, finalTotal, firstUnevaluated, guidedReducer, initialState, itemKey, unevaluatedCount
} from '../../lib/guidedFlow';

const UNDO_MS = 8000; // "Desfazer" do bloco marcado em lote

const reducedMotion = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
const isNarrow = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(max-width: 767px)').matches);

/**
 * Calculadora do MEEM em página única: os 11 blocos numa rolagem só, com a Régua de 30 fixa no topo.
 * Cada item é um controle segmentado [certo 1][errado 0]; sem resposta = "não avaliado" (conta 0).
 * "Já tenho o total" é o atalho de quem só quer digitar a pontuação.
 *
 * Privacidade: todo o estado fica em useReducer, na memória desta página. Nada vai para URL,
 * localStorage ou analytics. Os eventos levam apenas a posição no fluxo (nunca respostas,
 * escore, escolaridade ou e-mail).
 */
export default function MiniMentalCalculator({ heading = null, onResultVisibilityChange = () => {} }) {
  const [state, dispatch] = useReducer(guidedReducer, initialState);
  const [started, setStarted] = useState(false);
  const [totalError, setTotalError] = useState('');
  const headingRef = useRef(null);
  const scrollTimer = useRef(null);
  const lastBlockRef = useRef(null);
  const endBarRef = useRef(null);
  const [seenLast, setSeenLast] = useState(false); // o último bloco já entrou na tela
  const [endVisible, setEndVisible] = useState(false); // a barra do fim da página está na tela
  const [lastBatch, setLastBatch] = useState(null); // { blockId, prev } do último "Marcar bloco como correto"
  const batchTimer = useRef(null);
  const [pendingFirst, setPendingFirst] = useState(false); // vindo de "Ir ao primeiro" no resultado
  const [pendingEdit, setPendingEdit] = useState(false); // vindo de "Editar respostas"
  const stateRef = useRef(state);
  stateRef.current = state;

  const { mode, view, checked, batch } = state;
  const total = finalTotal(state);
  const blockScores = blockScoresFrom(checked);
  const pendentes = unevaluatedCount(checked);

  const act = (action) => {
    setStarted(true);
    dispatch(action);
  };

  useEffect(() => () => { if (scrollTimer.current) clearTimeout(scrollTimer.current); if (batchTimer.current) clearTimeout(batchTimer.current); }, []);

  useEffect(() => {
    if (!started) return;
    window.eidocAnalytics?.capture('calculator_started');
  }, [started]);

  useEffect(() => {
    onResultVisibilityChange(view === 'result');
    if (view === 'result') {
      window.dispatchEvent(new CustomEvent('minimental:result'));
      window.eidocAnalytics?.capture('calculator_result_viewed');
      window.eidocAnalytics?.capture('assessment_result_opened', {});
    }
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fechar a aba por engano apaga tudo (nada fica guardado de propósito): avisa antes de sair se há respostas.
  useEffect(() => {
    const warn = (e) => {
      const s = stateRef.current;
      if (Object.keys(s.checked).length === 0 && !s.totalInput) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  // Atalho R (fora de campos de texto) abre o resultado. A página não rola nem perde o foco ao abrir o modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (e.ctrlKey || e.metaKey || e.altKey || stateRef.current.view !== 'steps' || stateRef.current.mode !== 'guided') return; // no modo "Já tenho o total" o botão valida o número antes
      const t = e.target;
      if (t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      window.eidocAnalytics?.capture('calculator_used');
      dispatch({ type: 'showResult' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Celular: o botão fixo "Ver resultado" só aparece depois que o último bloco entra na tela
  // (e some quando a barra do fim da página já está visível).
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || mode !== 'guided') return undefined;
    const last = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting || entry.boundingClientRect.top < 0) setSeenLast(true);
    });
    const end = new IntersectionObserver(([entry]) => setEndVisible(entry.isIntersecting));
    if (lastBlockRef.current) last.observe(lastBlockRef.current);
    if (endBarRef.current) end.observe(endBarRef.current);
    return () => { last.disconnect(); end.disconnect(); };
  }, [view, mode]);

  const scrollToElement = (el) => {
    if (!el) return;
    el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  const answerItem = (key, value) => {
    const cur = stateRef.current;
    const [blockId] = key.split(':');
    const blockIndex = MEEM_BLOCKS.findIndex((b) => b.id === blockId);
    const blk = MEEM_BLOCKS[blockIndex];
    const next = { ...cur.checked };
    if (value === null) delete next[key]; else next[key] = value;
    act({ type: 'answer', key, value });
    if (lastBatch && lastBatch.blockId === blockId) clearBatchUndo();
    maybeScrollToNext(blk, blockIndex, cur.checked, next);
  };

  // Celular: ao completar um bloco, rolagem suave até o próximo. Desligada com prefers-reduced-motion.
  const maybeScrollToNext = (blk, blockIndex, prev, next) => {
    if (!isNarrow() || reducedMotion()) return;
    if (blockStatus(prev, blk) === 'complete' || blockStatus(next, blk) !== 'complete') return;
    const target = MEEM_BLOCKS[blockIndex + 1];
    if (!target) return;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => scrollToElement(document.getElementById(`bloco-${target.id}`)), 120);
  };

  const clearBatchUndo = () => {
    if (batchTimer.current) clearTimeout(batchTimer.current);
    batchTimer.current = null;
    setLastBatch(null);
  };

  // "Marcar bloco como correto (n)": guarda o estado anterior do bloco para o Desfazer (8s) e marca os itens "em lote".
  const batchBlock = (blockId) => {
    const blockIndex = MEEM_BLOCKS.findIndex((b) => b.id === blockId);
    const blk = MEEM_BLOCKS[blockIndex];
    const cur = stateRef.current;
    const prev = { checked: {}, batch: {} };
    const next = { ...cur.checked };
    blk.items.forEach((_, i) => {
      const key = itemKey(blk.id, i);
      if (cur.checked[key] !== undefined) prev.checked[key] = cur.checked[key];
      if (cur.batch[key]) prev.batch[key] = true;
      next[key] = true;
    });
    act({ type: 'allCorrect', blockId });
    if (batchTimer.current) clearTimeout(batchTimer.current);
    setLastBatch({ blockId, prev });
    batchTimer.current = setTimeout(() => { batchTimer.current = null; setLastBatch(null); }, UNDO_MS);
    maybeScrollToNext(blk, blockIndex, cur.checked, next);
  };

  const undoBatch = () => {
    if (!lastBatch) return;
    act({ type: 'restoreBlock', blockId: lastBatch.blockId, prev: lastBatch.prev });
    clearBatchUndo();
  };

  // Clique/toque num grupo da régua: rola até o primeiro bloco do domínio.
  const goToGroup = (group) => {
    const el = document.getElementById(`bloco-${group.blocks[0].id}`);
    if (el) scrollToElement(el);
  };

  // "Ir ao primeiro" (no modal): fecha o modal e leva ao primeiro item não avaliado.
  const goToFirstUnevaluated = () => { setPendingFirst(true); dispatch({ type: 'goto', step: 0 }); };
  const editAnswers = () => { setPendingEdit(true); dispatch({ type: 'goto', step: 0 }); };
  useEffect(() => {
    if (view !== 'steps' || (!pendingFirst && !pendingEdit)) return;
    const wasFirst = pendingFirst;
    setPendingFirst(false); setPendingEdit(false);
    const first = wasFirst ? firstUnevaluated(stateRef.current.checked) : null;
    const el = first ? document.getElementById(itemDomId(first.blockId, first.index)) : document.getElementById(itemDomId(MEEM_BLOCKS[0].id, 0));
    if (!el) { document.getElementById('total-informado')?.focus({ preventScroll: true }); return; }
    el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    el.querySelector('button')?.focus({ preventScroll: true });
  }, [pendingFirst, pendingEdit, view]);

  const showResultFromTotal = () => {
    const raw = String(state.totalInput).trim();
    const n = Number(raw);
    if (raw === '' || !Number.isInteger(n) || n < 0 || n > MEEM_TOTAL_MAX) {
      setTotalError(`Informe um número de 0 a ${MEEM_TOTAL_MAX}`);
      return;
    }
    setTotalError('');
    window.eidocAnalytics?.capture('calculator_used');
    act({ type: 'showResult' });
  };

  // O botão "Ver resultado" leva data-analytics-event="calculator_used" (capturado pelo rastreador, sem propriedades).
  const showResult = () => act({ type: 'showResult' });

  const guided = mode === 'guided';
  const lastBlock = MEEM_BLOCKS[MEEM_BLOCKS.length - 1];
  const leading = MEEM_BLOCKS.slice(0, -1);

  return (
    <section id="calculadora" aria-label="Calculadora do Mini Mental" className="relative scroll-mt-16">
      {guided && <StickyRuler checked={checked} total={total} onGroup={goToGroup} />}

      <div className="mx-auto max-w-[820px] px-4 min-[1180px]:grid min-[1180px]:max-w-[1180px] min-[1180px]:grid-cols-[minmax(0,1fr)_300px] min-[1180px]:gap-x-10">
        <div className="min-w-0 min-[1180px]:col-span-2">
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 pb-4 pt-6 md:pt-8">
              {heading}
              <div className="flex items-center gap-1 no-print">
                <button
                  type="button"
                  onClick={() => { setTotalError(''); act({ type: 'setMode', mode: guided ? 'total' : 'guided' }); }}
                  className="min-h-11 rounded-[6px] px-2 text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline hover:bg-[var(--hover)]"
                >
                  {guided ? 'Já tenho o total' : 'Voltar aos itens'}
                </button>
                {guided && (
                  <button type="button" data-analytics-event="calculator_used" onClick={showResult} className="min-h-11 rounded-[6px] px-2 text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline hover:bg-[var(--hover)] min-[1180px]:hidden">
                    Ver resultado
                  </button>
                )}
              </div>
            </div>
        </div>

        <div className="min-w-0">
          <>
            <div>
              <h2 className="sr-only">Domínios do teste</h2>
              <div className="space-y-3">
                {guided && leading.map((block) => (
                  <BlockSection key={block.id} block={block} checked={checked} batch={batch} onAnswer={answerItem} onBatch={batchBlock} undo={lastBatch?.blockId === block.id} onUndo={undoBatch} />
                ))}
                {guided && (
                  <div ref={lastBlockRef}>
                    <BlockSection block={lastBlock} checked={checked} batch={batch} onAnswer={answerItem} onBatch={batchBlock} undo={lastBatch?.blockId === lastBlock.id} onUndo={undoBatch} />
                  </div>
                )}
              </div>
            </div>
            {guided ? (
              <>
                <div ref={endBarRef} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-4 no-print">
                  <p role="status" className="text-[length:var(--fs-14)] text-[var(--ink-2)]">
                    {pendentes > 0 ? `${pendentes} ${pendentes === 1 ? 'item não avaliado' : 'itens não avaliados'} — o total pode estar subestimado.` : ''}
                  </p>
                  <button id="ver-resultado-fim" type="button" aria-keyshortcuts="R" data-analytics-event="calculator_used" onClick={showResult} className="btn-primary">Ver resultado</button>
                </div>
              </>
            ) : (
              <div className="border-t border-[var(--line)] py-6">
                <label htmlFor="total-informado" className="mb-2 block text-[length:var(--fs-15)] font-semibold">
                  Pontuação total obtida (0 a {MEEM_TOTAL_MAX})
                </label>
                <div className="flex flex-wrap items-start gap-3">
                  <input
                    id="total-informado"
                    ref={headingRef}
                    type="number"
                    min="0"
                    max={MEEM_TOTAL_MAX}
                    step="1"
                    inputMode="numeric"
                    value={state.totalInput}
                    placeholder="0"
                    aria-invalid={totalError ? 'true' : undefined}
                    aria-describedby={totalError ? 'total-erro' : undefined}
                    onChange={(e) => { setTotalError(''); act({ type: 'setTotal', value: e.target.value }); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') showResultFromTotal(); }}
                    className="input-field score-figure w-32 text-center text-[1.75rem]"
                  />
                  <button id="ver-resultado-fim" type="button" onClick={showResultFromTotal} className="btn-primary">Ver resultado</button>
                </div>
                {totalError && <p id="total-erro" role="alert" className="mt-2 text-[length:var(--fs-15)] font-medium text-[var(--miss)]">{totalError}</p>}
              </div>
            )}
          </>

        <h2 className="sr-only">Resumo, privacidade e metodologia</h2>
        <p className="mt-8 max-w-[70ch] text-[length:var(--fs-13)] leading-relaxed text-[var(--ink-2)]">
          <strong className="font-semibold text-[var(--ink)]">Privacidade:</strong> nenhuma informação pessoal ou pontuação é enviada ao servidor ou armazenada localmente. O cálculo é processado inteiramente em seu navegador.{' '}
          <strong className="font-semibold text-[var(--ink)]">Operacionalização:</strong> baseada em Brucki SMD, Nitrini R, Caramelli P, Bertolucci PHF, Okamoto IH. Sugestões para o uso do Mini-Exame do Estado Mental no Brasil. Arq Neuropsiquiatr. 2003;61(3-B):777-781.{' '}
          <a href="https://doi.org/10.1590/S0004-282X2003000500014" className="text-[var(--ink)] underline" rel="noopener">DOI</a>.{' '}
          Checklist de pontuação para uso por profissional de saúde. Não é o formulário oficial do MMSE, não é versão licenciada e não fornece diagnóstico. Você aplica o teste; esta ferramenta soma e contextualiza os pontos.
        </p>
        </div>

        {/* Painel de pontuação: só no desktop largo, no lugar da régua do topo. O banner "calculadora-margem" mora no fim dele. */}
        {guided && (
          <aside aria-label="Painel de pontuação" className="hidden min-[1180px]:block no-print">
            <ScorePanel checked={checked} total={total} pendentes={pendentes} onGroup={goToGroup} onShowResult={showResult}>
              {doutoBannersEnabled() && view !== 'result' && (
                <div className="mt-5" data-margem-track>
                  <DoutoBanner placement="calculadora-margem" variant="lateral" />
                </div>
              )}
            </ScorePanel>
          </aside>
        )}
      </div>

      <ResultModal
        open={view === 'result'}
        onClose={() => dispatch({ type: 'goto', step: 0 })}
        total={total}
        education={effectiveEducation(state)}
        educationAnswered={state.education !== null}
        reference={state.reference}
        ageConfirm={state.ageConfirm}
        checked={checked}
        batch={batch}
        blockScores={blockScores}
        mode={guided ? 'domains' : 'total'}
        unevaluated={guided ? pendentes : 0}
        onEducation={(value) => { if (value !== null) window.eidocAnalytics?.capture('calculator_schooling_chosen'); dispatch({ type: 'setEducation', value }); }}
        onReference={(value) => dispatch({ type: 'setReference', value })}
        onAgeConfirm={(value) => dispatch({ type: 'setAgeConfirm', value })}
        onEdit={editAnswers}
        onGoFirst={goToFirstUnevaluated}
        onReset={() => { setTotalError(''); dispatch({ type: 'reset' }); setStarted(false); }}
      />

      {/* Botão fixo (só no celular): depois que o último bloco entrou na tela e enquanto a barra do fim não aparece. */}
      {guided && view === 'steps' && seenLast && !endVisible && (
        <div className="fixed inset-x-0 bottom-[var(--novidades-h,0px)] z-40 border-t border-[var(--line)] bg-[var(--bg)] px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 md:hidden no-print">
          {pendentes > 0 && (
            <p className="pb-1 text-[length:var(--fs-13)] text-[var(--ink-2)]">
              {pendentes} {pendentes === 1 ? 'item não avaliado' : 'itens não avaliados'} — o total pode estar subestimado.
            </p>
          )}
          <button type="button" data-analytics-event="calculator_used" onClick={showResult} className="btn-primary w-full">Ver resultado</button>
        </div>
      )}
    </section>
  );
}
